import { useEffect, useRef, useState } from 'react';

interface SmoothCounterOptions {
  duration?: number;
}

export function useSmoothCounter(target: number, options: SmoothCounterOptions = {}): number {
  const { duration = 1200 } = options;
  const [displayed, setDisplayed] = useState(target);
  // Currently rendered value — an interrupted animation must resume from what
  // the user sees, not from the previous target, or a cancelled first frame
  // gets frozen on screen when the next target equals the old one.
  const displayedRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduceMotion) {
      displayedRef.current = target;
      setDisplayed(target);
      return;
    }

    const startValue = displayedRef.current;
    const endValue = target;
    if (startValue === endValue) return;

    // Snap on big downward jumps. Happens at phase boundaries when the displayed
    // counter is the phase-specific counter that resets to 0 for each new phase.
    // Animating from 48 → 0 creates a jarring "rolling back" effect.
    if (endValue < startValue && (startValue - endValue) > 5) {
      displayedRef.current = endValue;
      setDisplayed(endValue);
      return;
    }

    const startTime = performance.now();
    const tick = (now: number) => {
      // rAF timestamps are frame-start times and can precede startTime — clamp
      // so the easing never undershoots startValue (showed "-3 / 90 Produkte").
      const elapsed = Math.max(0, now - startTime);
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = startValue + (endValue - startValue) * eased;
      displayedRef.current = value;
      setDisplayed(value);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [target, duration]);

  return displayed;
}
